import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Clock, Coins, Award, ChevronDown, CheckCircle2, Check, Play,
  Users, HeartHandshake, Video, Lock, UserCheck, Search, FlaskConical,
  GraduationCap, ShoppingBag, Calendar, Radio, BadgeCheck,
  Monitor, BookOpen, TrendingUp, X, ArrowRight, ArrowDown, ChevronLeft, ChevronRight,
} from 'lucide-react';
import Wordmark from '../components/Wordmark';
import CourseCard from '../components/CourseCard';
import FeaturedCourseCard from '../components/FeaturedCourseCard';
import SignUpModal from '../components/SignUpModal';
import CheckoutModal from '../components/CheckoutModal';
import BioModal, { BioButton } from '../components/BioModal';
import { isLiveLike } from '../lib/course-types';
import { formatPromoEnd, promoModeLabel } from '../lib/promo';
import { useScrollLock, lockScroll } from '../lib/scroll-lock';
import { smoothScrollTo } from '../lib/scroll';

const FAQS = [
  {
    q: 'How long do I have access to a course?',
    a: 'Forever. Once you purchase, the course stays in your dashboard for unlimited access — no subscription, no time limit.',
  },
  {
    q: 'Do your certificates count for CEUs?',
    a: 'Many of our courses are designed with continuing-education credit in mind. Each course page lists the specific CEU eligibility. Always confirm with your licensing board that a given certificate satisfies their requirements.',
  },
  {
    q: 'What format are the courses?',
    a: 'Courses are self-paced and fully online — a mix of video, interactive scenarios, and knowledge checks. They run in your browser; nothing to install.',
  },
  {
    q: "Can I get a refund if a course isn't right for me?",
    a: "Yes — contact us within 14 days of purchase if you haven't completed the course and we'll process a full refund.",
  },
  {
    q: 'Are tutorials really free?',
    a: 'Yes. Our tutorials cover software walk-throughs, compliance basics, and productivity tips. No payment, no email wall — just create a free account and start watching.',
  },
];

const TABS = [
  {
    key: 'counseling',
    icon: Users,
    label: 'Counseling',
    intro:
      'These courses focuses on the core elements that make counseling effective, including',
    accent: 'presence, trust, ethics, and rapport',
    outro:
      '. Learners explore practical strategies to strengthen therapeutic alliances and confidently navigate common clinical challenges.',
    bullets: [
      'Building and maintaining therapeutic rapport',
      'Ethical presence and professional boundaries',
      'Strengthening the counseling foundation that supports all clinical work',
    ],
  },
  {
    key: 'mi',
    icon: HeartHandshake,
    label: 'MI',
    intro:
      'These courses engage learners in the spirit and skills of Motivational Interviewing, emphasizing',
    accent: 'collaborative, client-centered change conversations',
    outro:
      '. Participants gain concrete tools to evoke motivation, reduce resistance, and support client autonomy through practice-based learning.',
    bullets: [
      'Core MI principles and techniques',
      'Eliciting change talk and reducing resistance',
      'Supporting sustainable behavior change',
    ],
  },
  {
    key: 'telehealth',
    icon: Video,
    label: 'Telehealth',
    intro: 'These courses prepare helping professionals to deliver',
    accent: 'ethical, effective, and client-centered care',
    outro:
      ' in virtual settings. Learners examine real-world telehealth scenarios while gaining practical guidance on navigating the opportunities and limitations of remote service delivery.',
    bullets: [
      'Ethical and legal considerations in telehealth',
      'Client engagement in virtual environments',
      'Best practices for modern clinical service delivery',
    ],
  },
  {
    key: 'hipaa',
    icon: Lock,
    label: 'HIPAA',
    intro:
      'These courses break down complex HIPAA regulations into clear, relatable concepts. Learners gain essential knowledge to',
    accent: 'protect client privacy, reduce organizational risk, and maintain compliance',
    outro: ' with confidence.',
    bullets: [
      'HIPAA fundamentals and confidentiality requirements',
      'Risk reduction and compliance strategies',
      'Ethical responsibility in client data protection',
    ],
  },
  {
    key: 'clients',
    icon: UserCheck,
    label: 'Clients',
    intro:
      'These courses provide practical documentation strategies that support both clinical quality and regulatory standards. Learners develop skills to create',
    accent: 'clear, efficient, and defensible records',
    outro: ' that reflect professional competence.',
    bullets: [
      'Best practices for clinical documentation',
      'Compliance-aligned recordkeeping',
      'Reducing stress and risk around audits',
    ],
  },
  {
    key: 'micro',
    icon: Search,
    label: 'Micro Skills',
    intro:
      'These skills-focused courses emphasizes immediately applicable counseling micro-skills, including',
    accent: 'listening, questioning, reflection, and response techniques',
    outro:
      '. Learners strengthen the "how" of counseling to enhance everyday client interactions.',
    bullets: [
      'Core communication micro-skills',
      'Skill refinement through focused practice',
      'Enhancing ethical and effective client engagement',
    ],
  },
  {
    key: 'substances',
    icon: FlaskConical,
    label: 'Substances',
    intro: 'These courses address substance misuse through a',
    accent: 'compassionate, evidence-based lens',
    outro:
      '. Learners develop practical skills to assess risk, engage in effective conversations, and support behavior change without judgment.',
    bullets: [
      'Understanding substance misuse as complex behavior',
      'Risk assessment and supportive dialogue',
      'Expanding foundational counseling competencies',
    ],
  },
];

// Mock-data carousel for the "New Courses" section. Each card is either a
// reference to a LIVE_SESSIONS entry by id, or an async-course object with its
// own slug + metadata. Real data will replace this once the backend is wired.
const NEW_COURSE_CARDS = [
  { type: 'live', sessionId: 'mi-march-2026' },
  {
    type: 'async',
    id: 'cbt-fundamentals',
    slug: 'cbt-fundamentals',
    title: 'CBT Fundamentals for Counselors',
    description:
      'A practical introduction to cognitive-behavioral techniques, designed for clinicians integrating CBT into existing practice.',
    longDescription:
      'This self-paced course walks counselors through the core CBT model — identifying automatic thoughts, cognitive distortions, and behavioral experiments — with downloadable worksheets and real-session video demonstrations you can apply immediately.',
    image: '/training.jpg',
    duration: '4-hour course',
    price: '$149',
    whatYoullLearn: [
      'The ABC model and how to use it in session',
      'Common cognitive distortions and reframing techniques',
      'Behavioral activation planning for clients with depression',
      'Homework design that clients actually complete',
    ],
  },
  { type: 'live', sessionId: 'telehealth-april-2026' },
  {
    type: 'async',
    id: 'mindfulness-toolkit',
    slug: 'mindfulness-toolkit',
    title: 'Mindfulness Toolkit',
    description:
      'Evidence-based mindfulness practices counselors can teach clients in any setting — secular, accessible, ready-to-use.',
    longDescription:
      'Learn a curated set of mindfulness exercises — body scans, grounding techniques, and brief meditations — that you can teach clients in five minutes or less. Includes facilitator scripts, handouts, and guidance on adapting practices for trauma-informed settings.',
    image: '/workshop.jpg',
    duration: '3-hour course',
    price: '$99',
    whatYoullLearn: [
      'Five evidence-based mindfulness exercises for clinical use',
      'Adapting mindfulness for trauma-informed care',
      'Brief grounding techniques for acute anxiety',
      'Facilitator scripts and client handouts',
    ],
  },
  { type: 'live', sessionId: 'documentation-may-2026' },
];

const FEATURES = [
  'Self-paced learning',
  'Verifiable certificates',
  'CEU-eligible courses',
  'One-time purchase',
  'Browser-based — nothing to install',
  'Expert-designed curriculum',
];

function formatLiveTime(isoStr) {
  if (!isoStr) return null;
  return new Date(isoStr).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  }) + ' ET';
}

// Map a DB course row to the shape that LiveSessionCarousel / LiveSessionModal
// expect. Keeps the existing marketing UI unchanged while sourcing real data.
function dbCourseToSession(c) {
  return {
    id: c.id,
    slug: c.slug,
    title: c.title,
    description: c.short_description,
    longDescription: c.long_description_md,
    image: c.hero_image_url || '/liveworkshop.jpg',
    date: c.live_date || 'Date TBD',
    liveDateUtc: c.live_date_utc || null,
    format: c.duration_minutes ? `${c.duration_minutes}-min workshop` : 'Live workshop',
    instructor: c.hosted_by,
    instructorTitle: c.hosted_by_title,
    instructorBio: c.hosted_by_bio,
    instructorImage: c.hosted_by_image_url,
    whatYoullLearn: Array.isArray(c.what_youll_learn) ? c.what_youll_learn : [],
    price: c.price_cents === 0 ? 'Free' : `$${(c.price_cents / 100).toFixed(2)}`,
    price_cents: c.price_cents,
    promo: c.promo || null,      // derived promo pill (from an active discount code)
    courseType: c.course_type,
    inPersonCapacity: c.in_person_capacity,
  };
}

function dbCourseToAsyncCard(c) {
  const mins = c.duration_minutes;
  const duration = !mins ? 'Self-paced'
    : mins >= 60 ? `${Math.round((mins / 60) * 10) / 10}-hour course`
    : `${mins}-min course`;
  return {
    type: 'async',
    id: c.id,
    slug: c.slug,
    title: c.title,
    description: c.short_description,
    longDescription: c.long_description_md,
    image: c.hero_image_url || '/training.jpg',
    duration,
    price: c.price_cents === 0 ? 'Free' : `$${(c.price_cents / 100).toFixed(2)}`,
    whatYoullLearn: Array.isArray(c.what_youll_learn) ? c.what_youll_learn : [],
  };
}

// Take the N most recently created published courses and convert them to the
// card shape NewCoursesCarousel + NewCourseCard expect.
const COMING_SOON_CARDS = [
  {
    type: 'coming-soon',
    id: 'coming-soon-1',
    tagline: 'New courses are in development',
  },
  {
    type: 'coming-soon',
    id: 'coming-soon-2',
    tagline: 'More topics on the way',
  },
  {
    type: 'coming-soon',
    id: 'coming-soon-3',
    tagline: 'Check back soon for updates',
  },
];

function recentCoursesAsCards(courses, limit = 6) {
  const real = [...courses]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, limit)
    .map((c) =>
      isLiveLike(c.course_type)
        ? { type: 'live', id: c.id, session: dbCourseToSession(c) }
        : dbCourseToAsyncCard(c)
    );
  return [...real, ...COMING_SOON_CARDS];
}

// Legacy hardcoded marketing tiles — no longer rendered. Kept temporarily
// for reference; safe to delete once we're confident in the API-backed flow.
const LIVE_SESSIONS = [
  {
    id: 'mi-march-2026',
    title: 'Motivational Interviewing in Practice',
    description:
      'A hands-on workshop exploring real-world MI techniques with case discussions and live practice.',
    longDescription:
      'Spend 90 interactive minutes deepening your fluency in the spirit and skills of Motivational Interviewing. Through guided case examples and small-group practice, you\'ll leave with concrete techniques you can apply in your next session.',
    image: '/liveworkshop.jpg',
    date: 'March 15, 2026',
    format: '90-min workshop',
    instructor: 'Dr. Lena Marquez',
    instructorTitle: 'LCC',
    instructorBio:
      'Dr. Lena Marquez is a Licensed Clinical Counselor with over 15 years of experience in motivational interviewing and substance use treatment. She completed her doctoral work at the University of New Mexico, where she studied under one of the founders of MI. Lena has trained hundreds of clinicians across the country and is known for her warm, practical teaching style that bridges theory and real-world application.',
    whatYoullLearn: [
      'How to evoke and reinforce change talk in real conversations',
      'When and how to roll with sustain talk and ambivalence',
      'Practice strategies you can apply with clients the next day',
    ],
    price: '$99',
    seats: '12 seats remaining',
  },
  {
    id: 'telehealth-april-2026',
    title: 'Telehealth Ethics Roundtable',
    description:
      'Join experts and peers for a moderated discussion on the evolving ethics of remote clinical practice.',
    longDescription:
      'A moderated, peer-driven roundtable on the ethical questions clinicians face most often in virtual care — from informed consent to crisis response. Bring your own questions; leave with frameworks you can act on.',
    image: '/liveworkshop.jpg',
    date: 'April 9, 2026',
    format: '60-min Q&A',
    instructor: 'Panel of three licensed clinicians',
    whatYoullLearn: [
      'Current best practices for consent and confidentiality online',
      'Crisis response protocols across state lines',
      'Documentation considerations specific to telehealth',
    ],
    price: 'Free',
    seats: 'Open registration',
  },
  {
    id: 'documentation-may-2026',
    title: 'Defensible Documentation Workshop',
    description:
      'Practical strategies for writing clear, audit-ready clinical notes that protect both you and your clients.',
    longDescription:
      'A two-hour cohort workshop on writing clinical notes that hold up under audit and supervision. Includes guided rewrites of de-identified examples and a downloadable template you can adapt for your practice.',
    image: '/liveworkshop.jpg',
    date: 'May 4, 2026',
    format: '2-hour cohort',
    instructor: 'Marcus James, LMSW',
    whatYoullLearn: [
      'The structure auditors and supervisors actually look for',
      'How to document risk without overstating or understating',
      'Templates and shortcuts that save time without losing rigor',
    ],
    price: '$149',
    seats: '8 seats remaining',
  },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [profCtaRef, profCtaInView] = useInView(0.3);
  const [trustRef, trustInView] = useInView(0.2);
  const location = useLocation();

  useEffect(() => {
    fetch('/api/courses')
      .then((r) => r.json())
      .then((data) => setCourses(data.courses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch('/api/testimonials')
      .then((r) => (r.ok ? r.json() : { testimonials: [] }))
      .then((data) => setTestimonials(data.testimonials || []))
      .catch(() => {});
  }, []);

  // When the user arrived from a different page via a top-nav anchor click,
  // App's TopNav stashes the scroll target in location.state. Consume it
  // once on mount, then clear it so a back-button revisit doesn't refire.
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    requestAnimationFrame(() => smoothScrollTo(target));
    window.history.replaceState({}, '');
  }, [location.state]);

  const async_ = courses.filter((c) => !isLiveLike(c.course_type));
  const featured = courses.filter((c) => c.is_featured);
  const paid = async_.filter((c) => c.price_cents > 0);
  const free = async_.filter((c) => c.price_cents === 0);
  const liveSessions = courses
    .filter((c) => isLiveLike(c.course_type))
    // Hide sessions that have already ended. Auto-archive can leave them
    // published for up to 30 days while evaluations trickle in.
    .filter((c) => {
      if (!c.live_date_utc) return true;
      const end = new Date(c.live_date_utc).getTime() + (c.duration_minutes || 60) * 60000;
      return end > Date.now();
    })
    .map(dbCourseToSession)
    // Soonest session first; undated ("Date TBD") sessions sink to the end.
    .sort((a, b) => {
      const ta = a.liveDateUtc ? new Date(a.liveDateUtc).getTime() : Infinity;
      const tb = b.liveDateUtc ? new Date(b.liveDateUtc).getTime() : Infinity;
      return ta - tb;
    });

  return (
    <>
      {/* ── 1. Hero — two-column ──────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-light/60 via-white to-orange-50">
        <div className="max-w-6xl mx-auto px-4 pt-6 lg:pt-10 pb-10 lg:pb-12 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
              Professional Online Training
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-wordmark leading-[1.08]">
              <span className="block animate-hero-fade-up">Learn, Grow,</span>
              <span
                className="block text-brand animate-hero-fade-up text-6xl sm:text-7xl lg:text-8xl"
                style={{ animationDelay: '1s' }}
              >
                Level UP!
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
              Practical, self-paced courses for counselors, driving school professionals,
              and helping professionals. Earn verifiable certificates and build skills
              you can apply the next day.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <Monitor size={18} className="text-brand" strokeWidth={2.5} />
                <span className="text-sm font-semibold text-slate-700">Software Training</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen size={18} className="text-brand" strokeWidth={2.5} />
                <span className="text-sm font-semibold text-slate-700">Instructor Resources</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className="text-brand" strokeWidth={2.5} />
                <span className="text-sm font-semibold text-slate-700">Career Building</span>
              </div>
              <div className="flex items-center gap-2">
                <Award size={18} className="text-brand" strokeWidth={2.5} />
                <span className="text-sm font-semibold text-slate-700">Continuing Education Credits</span>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <a
                href="#courses"
                className="group inline-flex items-center gap-2 bg-brand text-white px-7 py-3.5 rounded-xl font-bold hover:bg-brand-dark shadow-lg shadow-brand/25 transition"
              >
                See What's New
                <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
              </a>
              <Link
                to="/tutorials"
                className="inline-block bg-white border-2 border-slate-200 text-slate-700 px-7 py-3.5 rounded-xl font-bold hover:border-brand hover:text-brand transition"
              >
                Free Tutorials
              </Link>
            </div>
            <div className="mt-7 flex items-center gap-3">
              <img
                src="/nbcc-acep-seal.png"
                alt="NBCC Approved Continuing Education Provider"
                className="w-16 h-16 lg:w-20 lg:h-20 object-contain flex-shrink-0"
              />
              <span className="text-sm font-semibold text-slate-700 leading-snug">
                NBCC Approved Continuing<br />Education Provider
              </span>
            </div>
          </div>

          <div className="hidden lg:block relative lg:col-span-3 aspect-[4/5]">
            <img
              src="/learnablehome_hero.jpg"
              alt="Professional at work"
              className="absolute inset-0 w-full h-full rounded-3xl shadow-2xl shadow-brand/15 object-cover"
            />
            <div
              className="absolute -bottom-6 -left-6 w-60 bg-brand border-4 border-white rounded-2xl shadow-lg p-5 space-y-4 animate-slide-in-left"
              style={{ animationDelay: '2.4s' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Award size={18} className="text-brand" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white leading-tight">Trusted by professionals</div>
                  <div className="text-[11px] text-white/85 leading-snug">Counselors, educators &amp; more</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Radio size={18} className="text-brand" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white leading-tight">Live &amp; On-Demand</div>
                  <div className="text-[11px] text-white/85 leading-snug">Interactive sessions + self-paced</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Mission — photo left, copy right ──────────────── */}
      <section className="pt-10 lg:pt-12 pb-10 lg:pb-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative pb-8 lg:pb-0">
            <img
              src="/training.jpg"
              alt="Training session"
              className="w-full rounded-3xl shadow-xl object-cover aspect-[5/4]"
            />
            <img
              src="/workshop.jpg"
              alt="Workshop"
              className="absolute -bottom-6 -right-6 w-48 rounded-2xl shadow-lg border-4 border-white hidden lg:block object-cover aspect-square"
            />
            {/* Gold trust card — mobile only (mirrors hero drop-in on desktop) */}
            <div className="absolute -bottom-4 left-4 right-4 bg-brand border-4 border-white rounded-2xl shadow-lg p-4 flex gap-4 lg:hidden">
              <div className="flex items-center gap-2.5 flex-1">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Award size={16} className="text-brand" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white leading-tight">Trusted by professionals</div>
                  <div className="text-[10px] text-white/85 leading-snug">Counselors, educators &amp; more</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5 flex-1">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Radio size={16} className="text-brand" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white leading-tight">Live &amp; On-Demand</div>
                  <div className="text-[10px] text-white/85 leading-snug">Interactive + self-paced</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
              Professional Development
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold leading-[1.1] text-wordmark">
              Take <span className="text-brand">ownership</span> of your learning path!
            </h2>
            <ul className="mt-7 space-y-5">
              <BulletItem>
                We guarantee an exceptional experience with flexible learning paths
                and professional guidance.
              </BulletItem>
              <BulletItem>
                Our courses are designed to{' '}
                <span className="text-brand font-semibold">engage, empower,</span> and{' '}
                <span className="text-brand font-semibold">enrich</span> professionals
                at every stage of their career.
              </BulletItem>
              <BulletItem>
                Each course focuses on{' '}
                <span className="text-brand font-semibold">LEARNABLE</span>{' '}
                that can be immediately applied to strengthen foundational
                competencies, expand your professional toolbox, and enhance the
                work you do with clients.
              </BulletItem>
            </ul>
            <button
              ref={profCtaRef}
              onClick={() => setSignUpOpen(true)}
              className={`inline-flex items-center gap-3 mt-8 bg-brand text-white pl-7 pr-3 py-3 rounded-full font-bold hover:bg-brand-dark shadow-lg shadow-brand/25 transition ${
                profCtaInView ? 'animate-slide-in-right' : 'opacity-0'
              }`}
            >
              Get Started
              <span className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center">
                <Play size={14} className="text-white fill-white ml-0.5" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ── 2b. Testimonials (admin-approved evaluation quotes; hidden when none) ── */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-orange-50/60 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
                What Learners Say
              </span>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900">
                Trusted by Professionals
              </h2>
              <p className="mt-3 text-slate-500">From post-session evaluations of our live courses.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              {testimonials.map((t) => (
                <figure
                  key={t.id}
                  className="w-full md:w-[calc((100%-3rem)/3)] flex flex-col bg-white rounded-3xl p-8 shadow-lg shadow-slate-900/5 border border-orange-100/60"
                >
                  <blockquote className="flex-1 text-slate-600 leading-relaxed">“{t.quote}”</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-brand/15 flex items-center justify-center text-brand font-bold text-sm">
                      {(t.name || '?')[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900">{t.name}</div>
                      {t.course_title && <div className="text-sm text-slate-500">{t.course_title}</div>}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 3. Featured course ──────────────────────────────── */}
      {featured.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-3xl font-extrabold text-wordmark">Featured Course</h2>
              <Link to="/library" className="text-sm text-brand font-semibold hover:underline">See all →</Link>
            </div>
            <FeaturedCourseCard course={featured[0]} />
          </div>
        </section>
      )}

      {/* ── 3. Live sessions ──────────────────────────────────── */}
      <section id="live" className="pt-16 pb-10 bg-gradient-to-b from-white to-orange-50/30 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-start">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
              <Radio size={14} /> Live & Interactive
            </span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-wordmark leading-tight">
              Real-time learning with <span className="text-brand">real experts</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Beyond our self-paced library, Learnable hosts live sessions throughout
              the year — cohort-based workshops, expert-led discussions, and interactive
              Q&amp;A on the topics that matter most to your practice.
            </p>
            <ul className="mt-7 space-y-5">
              <BulletItem>
                Cohort workshops with peer discussion and live feedback.
              </BulletItem>
              <BulletItem>
                Expert-led Q&amp;A on emerging topics in counseling and helping work.
              </BulletItem>
              <BulletItem>
                Continuing-education credits where applicable, with attendance verification.
              </BulletItem>
              <BulletItem>
                Micro learning follow-up modules are added at no additional cost.
              </BulletItem>
            </ul>
          </div>

          <LiveSessionCarousel sessions={liveSessions} />
        </div>
      </section>

      {/* ── 4. New courses ────────────────────────────────────── */}
      <section id="courses" className="bg-gradient-to-b from-orange-50/40 to-white py-16 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
            <div>
              <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-3">
                Just added
              </span>
              <h2 className="text-3xl font-extrabold text-wordmark">New Courses</h2>
            </div>
            <Link
              to="/library"
              className="group inline-flex items-center gap-2 text-sm font-bold text-brand hover:text-brand-dark transition-colors"
            >
              Browse the full library
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <NewCoursesCarousel cards={recentCoursesAsCards(courses)} />
          {free.length > 0 && (
            <div className="mt-10 text-center">
              <Link to="/tutorials" className="text-brand font-semibold hover:underline text-lg">
                Browse {free.length} free tutorial{free.length !== 1 ? 's' : ''} →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ── 5. Trust / pillar cards ───────────────────────────── */}
      <section className="py-16 bg-gradient-to-b from-brand-light from-60% to-white">
        <div ref={trustRef} className="max-w-6xl mx-auto px-4 grid sm:grid-cols-3 gap-6">
          <TrustCard
            icon={Clock}
            title="Self-paced"
            body="Learn on your schedule — pause, replay, and return anytime. Your progress is always saved."
            inView={trustInView}
            delay={0}
          />
          <TrustCard
            icon={Coins}
            title="One-time purchase"
            body="Buy once, access forever. No subscriptions, no recurring charges, no surprises."
            inView={trustInView}
            delay={350}
          />
          <TrustCard
            icon={Award}
            title="Verifiable certificates"
            body="Every passing learner earns a certificate with a public verification page."
            inView={trustInView}
            delay={700}
          />
        </div>
      </section>

      {/* ── 6. Credentialing social proof ─────────────────────── */}
      <section className="py-10 bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 flex flex-col items-center gap-5">
          <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full">
            Our Credentialing Partners
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 sm:gap-x-16">
            <img src="/nbcc-acep-seal.png" alt="NBCC Approved Continuing Education Provider" className="h-16 sm:h-20 w-auto object-contain" />
            <img src="/GACA-The-Association-for-Addiction-Professionals-4-3-1536x1536.png" alt="GACA" className="h-16 sm:h-20 w-auto object-contain" />
            <img src="/lpca+logo+-+new-1920w.webp" alt="LPCA" className="h-16 sm:h-20 w-auto object-contain" />
          </div>
        </div>
      </section>

      {/* ── 7. How it works ───────────────────────────────────── */}
      <section className="bg-white py-10 sm:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
              Simple Process
            </span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-wordmark">How It Works</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8">
            <HowStep
              icon={ShoppingBag}
              step="1"
              title="Pick a course"
              body="Browse the catalog. Pay once — no recurring fees, no surprises."
            />
            <HowStep
              icon={Lock}
              step="2"
              title="Set up your account"
              body="After checkout we email you a setup link. Choose a password, done."
            />
            <HowStep
              icon={GraduationCap}
              step="3"
              title="Earn your certificate"
              body="Complete the course at your own pace and download a verifiable PDF."
            />
          </div>
        </div>
      </section>

      {/* ── 8. Course-category tabs ────────────────────────── */}
      <CourseCategoryTabs />

      {/* ── 10. Flexible courses — stat grid left, features right  */}
      <section className="py-20 bg-gradient-to-br from-brand-light/40 via-orange-50/60 to-white">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <StatCard label="Course topics" value="Counseling" />
            <StatCard label="Learning style" value="Self-paced" />
            <StatCard label="Access duration" value="Lifetime" />
            <StatCard label="Pricing model" value="One-time" />
          </div>

          <div>
            <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
              Flexible Online Training
            </span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
              Flexible Online Courses for Busy Professionals
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Balancing work with continuing education has never been easier. Our
              courses are designed for busy professionals — access high-quality
              content and earn certificates on your own time, from anywhere.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {FEATURES.map((feat) => (
                <div key={feat} className="flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-brand flex-shrink-0" />
                  <span className="text-sm text-slate-700 font-medium">{feat}</span>
                </div>
              ))}
            </div>
            <a
              href="#courses"
              className="inline-block mt-8 bg-brand text-white px-7 py-3.5 rounded-xl font-bold hover:bg-brand-dark shadow-lg shadow-brand/25 transition"
            >
              Get Started
            </a>
          </div>
        </div>
      </section>

      {/* ── 12. FAQ with side image ───────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
              Common Questions
            </span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 space-y-3">
              {FAQS.map((f, i) => (
                <details key={i} className="group bg-gradient-to-r from-orange-50/60 to-white border border-orange-100 rounded-2xl">
                  <summary className="flex items-center justify-between cursor-pointer px-6 py-5 font-bold text-slate-900">
                    {f.q}
                    <ChevronDown size={18} className="text-brand transition-transform group-open:rotate-180 flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
            <div className="lg:col-span-2 hidden lg:block">
              <img
                src="/faq.jpg"
                alt="Professional learning"
                className="w-full rounded-3xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. Final CTA banner ────────────────────────────────── */}
      <section className="bg-gradient-to-br from-brand to-brand-dark text-white py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold">Ready to Level Up?</h2>
          <p className="mt-4 text-white/80 text-lg">
            Browse our catalog or start with a free tutorial today.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#courses"
              className="bg-white text-brand-dark px-7 py-3.5 rounded-xl font-bold hover:bg-slate-100 shadow-lg transition"
            >
              View Courses
            </a>
            <Link
              to="/tutorials"
              className="border-2 border-white/30 text-white px-7 py-3.5 rounded-xl font-bold hover:bg-white/10 transition"
            >
              Free Tutorials
            </Link>
          </div>
        </div>
      </section>

      {signUpOpen && <SignUpModal onClose={() => setSignUpOpen(false)} />}
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function BulletItem({ children }) {
  return (
    <li className="flex items-start gap-3 text-wordmark leading-relaxed">
      <span className="mt-1 w-5 h-5 rounded-full border-2 border-brand flex items-center justify-center flex-shrink-0">
        <Check size={12} className="text-brand" strokeWidth={3} />
      </span>
      <span>{children}</span>
    </li>
  );
}

function TabContent({ tab }) {
  return (
    <>
      <p className="text-lg text-wordmark leading-relaxed">
        {tab.intro}{' '}
        <span className="text-brand font-semibold">{tab.accent}</span>
        {tab.outro}
      </p>
      <ul className="mt-6 space-y-2 text-wordmark font-bold">
        {tab.bullets.map((b) => (
          <li key={b}>• {b}</li>
        ))}
      </ul>
    </>
  );
}

function TabModal({ tab, onClose }) {
  useScrollLock();

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center">
              <tab.icon size={20} />
            </span>
            <h3 className="text-lg font-bold text-slate-900">{tab.label}</h3>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5">
          <TabContent tab={tab} />
        </div>
      </div>
    </div>
  );
}

function CourseCategoryTabs() {
  const [active, setActive] = useState(TABS[0].key);
  const [mobileTab, setMobileTab] = useState(null);
  const tab = TABS.find((t) => t.key === active);
  const mobileTabData = mobileTab ? TABS.find((t) => t.key === mobileTab) : null;

  return (
    <section className="pt-4 pb-8 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-4 sm:mb-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-wordmark">
            Go ahead… <span className="text-brand">Explore!</span>
          </h2>
          <p className="mt-2 sm:mt-4 text-slate-500 text-sm sm:text-lg">
            Tap a topic below for more information.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-1.5 sm:gap-3 mb-4 sm:mb-10">
          {TABS.map((t) => {
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                onClick={() => {
                  setActive(t.key);
                  if (window.innerWidth < 768) setMobileTab(t.key);
                }}
                className={`flex flex-col items-center justify-center w-16 h-16 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl transition-all ${
                  isActive
                    ? 'bg-brand text-white shadow-lg shadow-brand/30'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                <t.icon size={20} className="sm:!w-[26px] sm:!h-[26px]" />
                <span className="mt-1 sm:mt-2 text-[10px] sm:text-sm font-bold">{t.label}</span>
              </button>
            );
          })}
        </div>

        <div
          key={active}
          className="hidden md:block animate-fade-up max-w-4xl mx-auto bg-white rounded-3xl shadow-xl shadow-brand/10 border border-orange-100 p-8 lg:p-10"
        >
          <TabContent tab={tab} />
        </div>
      </div>

      {mobileTabData && (
        <TabModal tab={mobileTabData} onClose={() => setMobileTab(null)} />
      )}
    </section>
  );
}

function NewCoursesCarousel({ cards = [] }) {
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const [visible, setVisible] = useState([]);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [openSession, setOpenSession] = useState(null);
  const [openCourse, setOpenCourse] = useState(null);

  const firstVisible = Math.max(0, visible.indexOf(true));

  // Cards snap to their left edge (scroll-padding keeps them aligned with the
  // section heading), so scrolling a card into view "start" pages the track.
  const scrollToCard = (i) => {
    cardRefs.current[i]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    // Courses load after the Coming Soon placeholders render. When they're
    // prepended, the browser re-snaps to the placeholder it was snapped to,
    // scrolling the real courses out of view — so always restart at card 1.
    track.scrollLeft = 0;
    let frame;
    const measure = () => {
      const t = track.getBoundingClientRect();
      setVisible(cardRefs.current.map((el) => {
        if (!el) return false;
        const r = el.getBoundingClientRect();
        const shown = Math.min(r.right, t.right) - Math.max(r.left, t.left);
        return shown >= r.width * 0.6;
      }));
      setAtStart(track.scrollLeft <= 2);
      setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 2);
    };
    const onChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    track.addEventListener('scroll', onChange, { passive: true });
    const resizeObserver = new ResizeObserver(onChange);
    resizeObserver.observe(track);
    measure();
    return () => {
      track.removeEventListener('scroll', onChange);
      resizeObserver.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [cards.length]);

  if (!cards.length) {
    return (
      <div className="rounded-2xl border border-orange-100/60 bg-white p-8 text-center text-sm text-slate-500">
        New courses are on the way. Check back soon.
      </div>
    );
  }

  const everythingFits = atStart && atEnd;

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-px-4 scrollbar-hide -mx-4 px-4 pt-1 pb-4"
      >
        {cards.map((card, i) => (
          <div
            key={card.id || card.sessionId}
            ref={(el) => (cardRefs.current[i] = el)}
            className="snap-start flex-shrink-0 w-[85%] sm:w-[calc((100%_-_1.25rem)/2)] lg:w-[calc((100%_-_2.5rem)/3)]"
          >
            <NewCourseCard card={card} onOpenLive={(s) => setOpenSession(s)} onOpenAsync={(c) => setOpenCourse(c)} />
          </div>
        ))}
      </div>

      {!everythingFits && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <button
            type="button"
            onClick={() => scrollToCard(Math.max(0, firstVisible - 1))}
            disabled={atStart}
            aria-label="Previous card"
            className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-brand hover:border-brand/40 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            {cards.map((c, i) => (
              <button
                key={c.id || c.sessionId}
                onClick={() => scrollToCard(i)}
                aria-label={`Show card ${i + 1}`}
                aria-current={visible[i] ? 'true' : undefined}
                className={`h-2 rounded-full transition-all ${
                  visible[i] ? 'w-6 bg-brand' : 'w-2 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => scrollToCard(Math.min(cards.length - 1, firstVisible + 1))}
            disabled={atEnd}
            aria-label="Next card"
            className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-brand hover:border-brand/40 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {openSession && (
        <LiveSessionModal session={openSession} onClose={() => setOpenSession(null)} />
      )}
      {openCourse && (
        <AsyncCourseModal course={openCourse} onClose={() => setOpenCourse(null)} />
      )}
    </div>
  );
}

function NewCourseCard({ card, onOpenLive, onOpenAsync }) {
  if (card.type === 'coming-soon') {
    return (
      <div className="h-full w-full bg-white rounded-2xl shadow-lg shadow-brand/10 overflow-hidden border border-dashed border-orange-200 flex flex-col">
        <div className="relative aspect-[5/4] bg-gradient-to-br from-orange-50 to-slate-50 flex items-center justify-center">
          <div className="text-center px-6">
            <Clock size={28} className="mx-auto text-brand/40 mb-2" />
            <span className="text-xs font-bold uppercase tracking-widest text-brand/60">Coming Soon</span>
          </div>
        </div>
        <div className="p-5 flex flex-col flex-1 items-center justify-center text-center">
          <p className="text-sm font-medium text-slate-500">{card.tagline}</p>
        </div>
      </div>
    );
  }

  if (card.type === 'live') {
    const s = card.session;
    if (!s) return null;
    return (
      <button
        type="button"
        onClick={() => onOpenLive(s)}
        className="h-full w-full text-left bg-white rounded-2xl shadow-lg shadow-brand/10 overflow-hidden border border-orange-100/60 flex flex-col hover:shadow-xl hover:shadow-brand/15 transition-all group cursor-pointer"
      >
        <div className="relative">
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-auto"
          />
          <div className="absolute top-3 left-3 bg-white rounded-full px-2.5 py-1 flex items-center gap-1.5 shadow-md">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-red-500" />
            </span>
            <span className="text-[9px] font-bold text-slate-900 tracking-widest uppercase">
              Live
            </span>
          </div>
        </div>
        <div className="p-4 flex flex-col flex-1 min-h-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium text-slate-500">
            <span className="inline-flex items-center gap-1 text-brand font-semibold">
              <Calendar size={12} /> {s.date}
            </span>
            {formatLiveTime(s.liveDateUtc) && (
              <>
                <span className="text-slate-300">•</span>
                <span className="inline-flex items-center gap-1 font-semibold">
                  <Clock size={12} /> {formatLiveTime(s.liveDateUtc)}
                </span>
              </>
            )}
            <span className="text-slate-300">•</span>
            <span>{s.format}</span>
          </div>
          <h3 className="mt-2 text-base font-bold text-slate-900 leading-tight line-clamp-2 group-hover:text-brand transition-colors">
            {s.title}
          </h3>
          <p className="mt-1.5 text-sm text-slate-600 line-clamp-3 flex-1">
            {s.description}
          </p>
          <span className="mt-3 self-start inline-flex items-center gap-1.5 text-sm font-bold text-brand group-hover:text-brand-dark transition-colors">
            View details
            <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </button>
    );
  }

  // Async course card
  return (
    <button
      type="button"
      onClick={() => onOpenAsync(card)}
      className="h-full w-full text-left bg-white rounded-2xl shadow-lg shadow-brand/10 overflow-hidden border border-orange-100/60 flex flex-col hover:shadow-xl hover:shadow-brand/15 transition-all group"
    >
      <div className="relative">
        <img
          src={card.image}
          alt={card.title}
          className="w-full h-auto"
        />
        <div className="absolute top-3 left-3 bg-white rounded-full px-2.5 py-1 flex items-center gap-1.5 shadow-md">
          <Clock size={11} className="text-brand" />
          <span className="text-[9px] font-bold text-slate-900 tracking-widest uppercase">
            {card.duration}
          </span>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Self-paced course
        </span>
        <h3 className="mt-2 text-base font-bold text-slate-900 leading-tight line-clamp-2 group-hover:text-brand transition-colors">
          {card.title}
        </h3>
        <p className="mt-1.5 text-sm text-slate-600 line-clamp-2 flex-1">{card.description}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-bold text-brand">{card.price}</span>
          <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 group-hover:text-brand transition-colors">
            View
            <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </button>
  );
}

function LiveSessionCarousel({ sessions = [] }) {
  const [expanded, setExpanded] = useState(sessions[0]?.id);
  const [openSession, setOpenSession] = useState(null);

  // Keep expanded card in sync once API data arrives.
  useEffect(() => {
    if (!expanded && sessions.length) setExpanded(sessions[0].id);
  }, [sessions, expanded]);

  if (!sessions.length) {
    return (
      <div className="rounded-2xl border border-orange-100/60 bg-white p-6 text-center text-sm text-slate-500">
        Live sessions coming soon. Check back shortly.
      </div>
    );
  }

  return (
    <div className="space-y-3 min-w-0">
      {sessions.map((session) => {
        const isOpen = expanded === session.id;
        return (
          <article
            key={session.id}
            className={`bg-white rounded-2xl shadow-lg shadow-brand/10 border transition-all ${
              isOpen ? 'border-brand/30 shadow-xl shadow-brand/15' : 'border-orange-100/60'
            }`}
          >
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : session.id)}
              className="w-full text-left p-4 flex items-center gap-4 min-w-0"
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-brand/10 flex flex-col items-center justify-center">
                <span className="relative flex w-1.5 h-1.5 mb-1">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-red-500" />
                </span>
                <span className="text-[8px] font-bold text-brand tracking-widest uppercase">Live</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-slate-900 leading-tight truncate">{session.title}</h3>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1 text-brand font-semibold">
                    <Calendar size={11} /> {session.date}
                  </span>
                  {formatLiveTime(session.liveDateUtc) && (
                    <>
                      <span className="text-slate-300">•</span>
                      <span className="inline-flex items-center gap-1 font-semibold">
                        <Clock size={11} /> {formatLiveTime(session.liveDateUtc)}
                      </span>
                    </>
                  )}
                  <span className="text-slate-300">•</span>
                  <span>{session.format}</span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-3">
                <span className="hidden sm:inline text-xs font-bold text-slate-900 bg-brand-light px-2.5 py-1 rounded-full">
                  {session.price}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </div>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 animate-fade-up">
                <div className="rounded-xl overflow-hidden mb-3">
                  <img
                    src={session.image}
                    alt={session.title}
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {session.description}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="sm:hidden text-xs font-bold text-slate-900 bg-brand-light px-2.5 py-1 rounded-full">
                    {session.price}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpenSession(session)}
                    className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-brand-dark hover:gap-3 transition-all shadow-md shadow-brand/25"
                  >
                    View details
                    <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </article>
        );
      })}

      {openSession && (
        <LiveSessionModal
          session={openSession}
          onClose={() => setOpenSession(null)}
        />
      )}
    </div>
  );
}

function LiveSessionModal({ session, onClose }) {
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const bookable = !!session.slug;
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('learnable_user') || 'null'); }
    catch { return null; }
  })();

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(onClose, 320);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    const unlock = lockScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlock();
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={session.title}
      onClick={requestClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60"
      style={{
        animation: closing
          ? 'carouselFadeOut 0.3s ease-in forwards'
          : 'carouselFade 0.25s ease-out both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overscroll-contain"
        style={{
          animation: closing
            ? 'modalSlideDown 0.3s cubic-bezier(0.32, 0, 0.67, 0) forwards'
            : 'fadeUp 0.3s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        <div className="relative">
          <img
            src={session.image}
            alt={session.title}
            className="w-full h-auto rounded-t-3xl"
          />
          <div className="absolute top-4 left-4 bg-white rounded-full px-3 py-1.5 flex items-center gap-2 shadow-lg">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500" />
            </span>
            <span className="text-[10px] font-bold text-slate-900 tracking-widest uppercase">
              Live
            </span>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center text-slate-700 hover:text-slate-900 transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 lg:p-8 pb-8 lg:pb-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-slate-500">
              <span className="inline-flex items-center gap-1.5 text-brand font-semibold">
                <Calendar size={14} /> {session.date}
              </span>
              {formatLiveTime(session.liveDateUtc) && (
                <>
                  <span className="text-slate-300">•</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold">
                    <Clock size={14} /> {formatLiveTime(session.liveDateUtc)}
                  </span>
                </>
              )}
              <span className="text-slate-300">•</span>
              <span>{session.format}</span>
            </div>
            <span className="inline-flex items-center text-sm font-bold text-slate-900 bg-brand-light px-3 py-1 rounded-full">
              {session.price}
            </span>
          </div>

          <h2 className="mt-3 text-2xl lg:text-3xl font-extrabold text-slate-900 leading-tight">
            {session.title}
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            {session.longDescription || session.description}
          </p>

          {session.whatYoullLearn?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                What you'll learn
              </h3>
              <ul className="space-y-2">
                {session.whatYoullLearn.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {session.instructor && (
            <PresenterSection session={session} />
          )}

          <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-slate-100">
            {session.seats && (
              <span className="text-xs font-medium text-slate-500">
                {session.seats}
              </span>
            )}
            <div className="flex flex-col gap-3 sm:items-end sm:ml-auto w-full">
              {session.promo && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 w-full sm:w-auto">
                  <span className="text-red-600 text-lg font-extrabold">${(session.promo.special_price_cents / 100).toFixed(2)}</span>
                  <span className="text-red-500 text-sm line-through">${(session.promo.original_price_cents / 100).toFixed(2)}</span>
                  <span className="mx-1 text-red-300">|</span>
                  <span className="text-red-700 text-sm font-semibold">Code: <span className="font-mono tracking-wide">{session.promo.code}</span></span>
                  {promoModeLabel(session.promo) && (
                    <>
                      <span className="mx-1 text-red-300">|</span>
                      <span className="text-red-700 text-xs font-semibold">{promoModeLabel(session.promo)}</span>
                    </>
                  )}
                  {session.promo.expires_at && (
                    <>
                      <span className="mx-1 text-red-300">|</span>
                      <span className="text-red-600 text-xs font-semibold">{formatPromoEnd(session.promo.expires_at)}</span>
                    </>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <Link
                  to={`/courses/${session.slug}`}
                  className="px-5 py-2.5 rounded-full text-sm font-bold text-slate-600 hover:text-slate-900 border-2 border-slate-200 hover:border-slate-300 transition"
                >
                  View full details
                </Link>
                {bookable ? (
                  <button
                    type="button"
                    onClick={() => setShowCheckout(true)}
                    className="inline-flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-brand-dark shadow-lg shadow-brand/25 transition"
                  >
                    {session.price_cents === 0 ? 'Enroll now' : `Buy now · ${session.price}`}
                    <ArrowRight size={15} />
                  </button>
                ) : (
                  <span className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-bold text-slate-400 border-2 border-slate-200">
                    Coming soon
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {showCheckout && (
          <CheckoutModal
            course={{ id: session.id, slug: session.slug, title: session.title, price_cents: session.price_cents, course_type: session.courseType, in_person_capacity: session.inPersonCapacity }}
            currentUser={storedUser}
            onClose={() => setShowCheckout(false)}
            onSuccess={() => navigate('/dashboard')}
          />
        )}
      </div>
    </div>
  );
}

function PresenterSection({ session }) {
  const [bioOpen, setBioOpen] = useState(false);

  return (
    <div className="mt-6 pt-5 border-t border-slate-100">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand/15 flex items-center justify-center text-brand font-bold text-sm">
          {session.instructor[0]}
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">
            Hosted by
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-900">
              {session.instructor}{session.instructorTitle ? `, ${session.instructorTitle}` : ''}
            </span>
            {session.instructorBio && <BioButton onClick={() => setBioOpen(true)} />}
          </div>
        </div>
      </div>
      {bioOpen && session.instructorBio && (
        <BioModal
          name={session.instructor}
          title={session.instructorTitle}
          imageUrl={session.instructorImage}
          bio={session.instructorBio}
          onClose={() => setBioOpen(false)}
        />
      )}
    </div>
  );
}

function AsyncCourseModal({ course, onClose }) {
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(onClose, 320);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    const unlock = lockScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlock();
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={course.title}
      onClick={requestClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60"
      style={{
        animation: closing
          ? 'carouselFadeOut 0.3s ease-in forwards'
          : 'carouselFade 0.25s ease-out both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overscroll-contain"
        style={{
          animation: closing
            ? 'modalSlideDown 0.3s cubic-bezier(0.32, 0, 0.67, 0) forwards'
            : 'fadeUp 0.3s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        <div className="relative aspect-video">
          <img
            src={course.image}
            alt={course.title}
            className="absolute inset-0 w-full h-full object-cover rounded-t-3xl"
          />
          <div className="absolute top-4 left-4 bg-white rounded-full px-3 py-1.5 flex items-center gap-2 shadow-lg">
            <Clock size={13} className="text-brand" />
            <span className="text-[10px] font-bold text-slate-900 tracking-widest uppercase">
              {course.duration}
            </span>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center text-slate-700 hover:text-slate-900 transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 lg:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Self-paced course
            </span>
            <span className="inline-flex items-center text-sm font-bold text-slate-900 bg-brand-light px-3 py-1 rounded-full">
              {course.price}
            </span>
          </div>

          <h2 className="mt-3 text-2xl lg:text-3xl font-extrabold text-slate-900 leading-tight">
            {course.title}
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            {course.longDescription || course.description}
          </p>

          {course.whatYoullLearn?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                What you'll learn
              </h3>
              <ul className="space-y-2">
                {course.whatYoullLearn.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-slate-100">
            <div className="flex gap-3 sm:ml-auto">
              <button
                type="button"
                onClick={requestClose}
                className="px-5 py-2.5 rounded-full text-sm font-bold text-slate-600 hover:text-slate-900 border-2 border-slate-200 hover:border-slate-300 transition"
              >
                Cancel
              </button>
              <Link
                to={`/courses/${course.slug}`}
                className="inline-flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-brand-dark shadow-lg shadow-brand/25 transition"
              >
                Enroll · {course.price}
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustCard({ icon: Icon, title, body, inView = true, delay = 0 }) {
  return (
    <div
      className={`bg-white rounded-3xl p-8 shadow-lg shadow-slate-900/5 border border-orange-100/60 text-center ${
        inView ? 'animate-slide-up' : 'opacity-0'
      }`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand/15 flex items-center justify-center mb-4">
        <Icon size={26} className="text-brand" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-slate-900/5 border border-orange-100/60">
      <div className="text-2xl font-extrabold text-brand">{value}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide font-medium">{label}</div>
    </div>
  );
}

function HowStep({ icon: Icon, step, title, body }) {
  return (
    <div className="text-center">
      <div className="relative w-16 h-16 mx-auto mb-5">
        <div className="w-16 h-16 rounded-2xl bg-brand/15 flex items-center justify-center">
          <Icon size={28} className="text-brand" />
        </div>
        <span className="absolute -top-2 -right-2 w-7 h-7 bg-brand text-white text-xs font-bold rounded-full flex items-center justify-center shadow">
          {step}
        </span>
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

